/**
 * useManageChannelsTemplate.ts
 */
import Channel from '@shared/classes/channel';
import ChannelUtils from '@shared/utils/channels';
import Content from '@shared/types/enums/content';
import Focus from '@shared/types/enums/focus';
import Storage from '@shared/utils/storage';
import TranslationManager from '@shared/assets/locale/translationsManager';
import closeMenu from '@shared/actions/menu/closeMenu';
import putChannelsOrder from '@shared/api/channels/putChannelsOrder';
import useKeyboard from '@shared/components/utils/hooks/useKeyboard';
import { IChannelOrder } from '@shared/types/interfaces/channel';
import { initChannels } from '@shared/actions/channels/initChannels';
import { loadChannels as loadVisibleChannels } from '@shared/store/thunks/channels.thunk';
import { popRoute } from '@shared/store/thunks/route.thunk';
import { setFocus, selectFocus } from '@shared/store/slices/focus.slice';
import { useAppDispatch, useAppSelector } from '@shared/store/hooks';
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  channelsSelector,
  loadHiddenChannels,
} from '@shared/store/slices/channels.slice';

/**
 * Constants
 */
const LITERALS = 'PAGES.SETTINGS.MANAGE_CHANNELS';

/**
 * Hook to use the manage channels template.
 * Handles the manage channels template logic.
 */
export default function useManageChannelsTemplate() {
  // ------------------------------------------------------------
  // Variables
  // ------------------------------------------------------------

  // Dispatch
  const dispatch = useAppDispatch();

  // Selectors
  const { channelsData, hiddenChannels } = useAppSelector(channelsSelector);
  const focus = useAppSelector(selectFocus);

  // State
  const [editing, setEditing] = useState(false);

  // Refs
  const allChannelsDataRef = useRef<Channel[]>(
    ChannelUtils.getAllChannels(true, true)
  );
  const currentChannelRef = useRef<Channel>();
  const editingRef = useRef<boolean>(editing);
  const focusRef = useRef(focus);
  const originalChannelsRef = useRef(allChannelsDataRef.current);
  editingRef.current = editing;
  focusRef.current = focus;

  // ------------------------------------------------------------
  // Callbacks
  // ------------------------------------------------------------

  const returnAction = () => {
    dispatch(popRoute());
  };

  const acceptAction = () => {
    originalChannelsRef.current = allChannelsDataRef.current;
    allChannelsDataRef.current.filter((item) => !item.hidden);
    const storageChannels = allChannelsDataRef.current.map((item, index) => {
      return { id: item?.id, order: index };
    });
    const orderedChannels = allChannelsDataRef.current.map((item) => {
      return {
        channelCode: item.code,
        dial: item.dial,
        hidden: item.hidden,
      } as IChannelOrder;
    });
    Storage.set('channelOrder', JSON.stringify(storageChannels));
    putChannelsOrder(orderedChannels);
    returnAction();
  };

  const restoreAction = () => {
    originalChannelsRef.current = [];
    Storage.delete('channelOrder');
    initChannels(true);
    returnAction();
  };

  const cancelAction = () => {
    returnAction();
  };

  // ------------------------------------------------------------
  // Event handlers
  // ------------------------------------------------------------

  const onChange = (
    channel: Channel | undefined,
    increment: number | undefined
  ) => {
    if (editingRef.current) {
      if (!currentChannelRef?.current) {
        currentChannelRef.current = allChannelsDataRef.current?.length
          ? allChannelsDataRef.current[0]
          : undefined;
      }
      const itemIdx = allChannelsDataRef.current?.findIndex(
        (item) => item.id === currentChannelRef?.current?.id
      );
      if (itemIdx !== -1) {
        const items = [...(allChannelsDataRef.current as Channel[])];
        items.splice(itemIdx as number, 1);
        items.splice(
          (itemIdx as number) + (increment || 0),
          0,
          currentChannelRef?.current as Channel
        );
        items.forEach((item, index) => {
          item.dial = index + 1;
        });
        allChannelsDataRef.current = items;
        const hiddenChannels = items.filter((item) => item.hidden);
        const visibleChannels = items.filter(
          (item) => !item.hidden && item.type === Content.CHANNEL
        );
        dispatch(loadHiddenChannels(hiddenChannels));
        dispatch(loadVisibleChannels(visibleChannels));
      }
    } else {
      currentChannelRef.current = channel;
    }
  };

  // ------------------------------------------------------------
  // Effect hooks
  // ------------------------------------------------------------

  useEffect(() => {
    dispatch(setFocus(Focus.CHANNEL_LIST));
    return () => {
      setEditing(false);
      const hiddenChannels = originalChannelsRef.current.filter(
        (item) => item.hidden
      );
      const visibleChannels = originalChannelsRef.current.filter(
        (item) => !item.hidden && item.type === Content.CHANNEL
      );
      dispatch(loadHiddenChannels(hiddenChannels));
      dispatch(loadVisibleChannels(visibleChannels));
    };
  }, []);

  useEffect(() => {
    if (channelsData?.length && !originalChannelsRef.current.length) {
      originalChannelsRef.current = channelsData;
    }
  }, [channelsData, hiddenChannels]);

  // ------------------------------------------------------------
  // Keyboard hooks
  // ------------------------------------------------------------

  // Channel list keyboard
  useKeyboard({
    focus: Focus.CHANNEL_LIST,
    keyDownHandler: (key) => {
      switch (key) {
        case 'LEFT':
          dispatch(setFocus(Focus.MENU));
          break;
        case 'RIGHT':
          dispatch(setFocus(Focus.MANAGE_CHANNELS_BUTTONS));
          break;
        case 'BACK':
          dispatch(popRoute());
          break;
        case 'SELECT':
          if (!editingRef.current) {
            setEditing(true);
          } else {
            setEditing(false);
          }
          break;
      }
    },
  });

  // Manage channels buttons keyboard
  useKeyboard({
    focus: Focus.MANAGE_CHANNELS_BUTTONS,
    keyDownHandler: (key) => {
      switch (key) {
        case 'BACK':
          dispatch(popRoute());
          break;
      }
    },
  });

  // Menu keyboard
  useKeyboard({
    focus: Focus.MENU,
    keyDownHandler: (key: string) => {
      switch (key) {
        case 'RIGHT':
          dispatch(setFocus(Focus.CHANNEL_LIST));
          break;
      }
    },
  });

  return useMemo(() => {
    return {
      display: allChannelsDataRef.current.length > 0,
      context: {
        editing,
        setEditing,
      },
      props: {
        buttonsContainer: {
          onMouseEnter: () => {
            closeMenu();
            dispatch(setFocus(Focus.MANAGE_CHANNELS_BUTTONS));
          },
        },
        buttonList: {
          focus: Focus.MANAGE_CHANNELS_BUTTONS,
          onChange: () => null,
          outOfBounds: (focus: Focus | number | string, key?: string) => {
            if (key === 'LEFT') {
              dispatch(setFocus(Focus.CHANNEL_LIST));
            }
          },
        },
        channelList: {
          channelsData: allChannelsDataRef.current,
          currentIndex: 0,
          focused: focus === Focus.CHANNEL_LIST,
          pop: false,
          preventAction: true,
          onChangeState: onChange,
        },
        acceptButton: {
          action: acceptAction,
          index: 0,
          text: `${LITERALS}.BUTTONS.ACCEPT`,
        },
        restoreButton: {
          action: restoreAction,
          index: 1,
          text: `${LITERALS}.BUTTONS.RESTORE`,
        },
        cancelButton: {
          action: cancelAction,
          index: 2,
          text: `${LITERALS}.BUTTONS.CANCEL`,
        },
      },
      literals: {
        order: TranslationManager.get(`${LITERALS}.ORDER`),
        pressOk: TranslationManager.get(`${LITERALS}.PRESS_OK`),
        move: TranslationManager.get(`${LITERALS}.MOVE`),
        pressOkToFix: TranslationManager.get(`${LITERALS}.PRESS_OK_TO_FIX`),
        finish: TranslationManager.get(`${LITERALS}.FINISH`),
      },
    };
  }, [editing, focus, setEditing]);
}
